import { createContext, useState, useEffect, useContext, ReactNode, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  subscribed: boolean;
  subscriptionTier: string | null;
  subscriptionEnd: string | null;
  signOut: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const { toast } = useToast();

  // Track if subscription check is in progress
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  // Track if initial auth check is complete
  const [isAuthCheckDone, setIsAuthCheckDone] = useState(false);
  // Debounce timer and last refresh timestamp
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(0);
  const DEBOUNCE_DELAY = 2000; // 2 seconds between refresh calls

  useEffect(() => {
    console.log("Auth provider initialized");
    
    // Set up the auth state listener first
    const { data: { subscription }} = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Use setTimeout to avoid potential deadlocks with onAuthStateChange
          setTimeout(() => {
            refreshSubscription();
          }, 0);
        } else {
          setSubscribed(false);
          setSubscriptionTier(null);
          setSubscriptionEnd(null);
          // Always set loading to false when we know there's no session
          setIsLoading(false);
          setIsAuthCheckDone(true);
        }
      }
    );

    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Initial session check:", session ? "Session found" : "No session");
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        refreshSubscription();
      } else {
        setIsLoading(false);
        setIsAuthCheckDone(true);
      }
    }).catch(error => {
      console.error("Error during initial session check:", error);
      // Always set loading to false on error
      setIsLoading(false);
      setIsAuthCheckDone(true);
    });

    // Safety timeout to ensure we don't get stuck in loading state
    const safetyTimer = setTimeout(() => {
      if (isLoading && !isAuthCheckDone) {
        console.log("Safety timeout triggered to exit loading state");
        setIsLoading(false);
        setIsAuthCheckDone(true);
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const refreshSubscription = async () => {
    if (!user) {
      // If there's no user, exit loading state and return
      setIsLoading(false);
      setIsAuthCheckDone(true);
      return;
    }
    
    // Prevent multiple simultaneous subscription checks
    if (isCheckingSubscription) return;
    
    // Debounce implementation - if called too frequently, delay execution
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshRef.current;
    
    if (timeSinceLastRefresh < DEBOUNCE_DELAY) {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      
      // Set a new timer to execute after the delay
      refreshTimerRef.current = setTimeout(() => {
        // When timer fires, update the timestamp and execute the refresh
        lastRefreshRef.current = Date.now();
        performRefresh();
      }, DEBOUNCE_DELAY - timeSinceLastRefresh);
      return;
    }
    
    // If enough time has passed, update timestamp and execute immediately
    lastRefreshRef.current = now;
    performRefresh();
  };
  
  const performRefresh = async () => {
    try {
      setIsCheckingSubscription(true);
      console.log("Refreshing subscription data");
      
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        method: 'GET',
      });
      
      if (error) {
        console.error('Error checking subscription:', error);
        // Continue despite error - don't return early
      }
      
      if (data) {
        // Only log when subscription data changes
        const hasChanged = 
          data.subscribed !== subscribed || 
          data.subscription_tier !== subscriptionTier || 
          data.subscription_end !== subscriptionEnd;
          
        if (hasChanged) {
          console.log("Subscription data received:", data);
        }
        
        setSubscribed(data.subscribed);
        setSubscriptionTier(data.subscription_tier);
        setSubscriptionEnd(data.subscription_end);
      }
      
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
    } finally {
      setIsCheckingSubscription(false);
      setIsLoading(false);
      setIsAuthCheckDone(true);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Error",
        description: "There was a problem signing you out.",
        variant: "destructive",
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        isAuthenticated: !!user,
        isLoading,
        subscribed,
        subscriptionTier,
        subscriptionEnd,
        signOut,
        refreshSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
